import { z } from "zod";

import {
  AIUserFeedbackSchema,
  AIUserFeedbackStatusSchema,
  submissionCommunicationMethodSchema,
} from "@smart/bridge-types-basic";
import { buildApiGatewaySchemas } from "@smart/itops-constructs-basic";

import { apiGateway } from "./api-gateway";
import {
  addressPredictionSchema,
  addressSchema,
  loadedFormSchema,
} from "./shared-schemas";

const submissionStatusSchema = z.enum([
  "draft",
  "active",
  "completed",
  "cancelled",
]);

export const createSubmissionLambdaSchema = {
  input: z.object({
    encodedParam: z.string().optional(),
    teamId: z.string().optional(),
    slugId: z.string().optional(),
  }),
  output: z.object({
    slug: z
      .object({
        uri: z.string(),
      })
      .nullable(),
    submission: z
      .object({
        uri: z.string(),
      })
      .nullable(),
    analyticsTools: z
      .array(z.object({ type: z.string(), trackingId: z.string() }))
      .optional(),
    operationId: z.string().optional(),
    unavailableReason: z.enum(["inactiveWebEnquiry"]).optional(),
  }),
};

export const loadLambdaSchema = {
  input: z.object({
    id: z.string(),
    returnToken: z.string().optional(),
  }),
  output: z.object({
    matter: z.object({ isClosed: z.boolean() }).optional(),
    form: loadedFormSchema.optional(),
    team: z
      .object({
        uri: z.string(),
        name: z.string(),
        email: z.string().optional(),
        address: z.string(),
        phone: z.string(),
        source: z.string(),
        picture: z.string().optional(),
      })
      .optional(),
    submission: z
      .object({
        uri: z.string(),
        teamUri: z.string(),
        responses: z.record(
          z.object({
            value: z.any(),
            updatedAt: z.string(),
          }),
        ),
        status: submissionStatusSchema.optional(),
        autoFillStatus: AIUserFeedbackStatusSchema.optional(),
        lastUpdatedFieldUri: z.string().optional(),
        lastUpdatedSectionUri: z.string().optional(),
        aiFillFiles: z.array(z.string()).optional(),
        deleted: z.boolean().optional(),
        updatedAt: z.string().optional(),
        communicationMethod: submissionCommunicationMethodSchema,
        recipient: z
          .object({
            firstName: z.string().optional().nullable(),
            lastName: z.string().optional().nullable(),
          })
          .optional(),
      })
      .optional(),
    staffDetails: z
      .array(
        z.object({
          id: z.string(),
          firstName: z.string().optional().nullable(),
          middleName: z.string().optional().nullable(),
          lastName: z.string().optional().nullable(),
          busySlots: z
            .array(
              z.object({
                id: z.string(),
                startTime: z.string(),
                endTime: z.string(),
                timezone: z.string().optional().nullable(),
              }),
            )
            .optional(),
        }),
      )
      .optional(),
    aiUserFeedback: AIUserFeedbackSchema.optional(),
    branding: z
      .object({
        brandColor: z.string().optional(),
        affectedAreas: z
          .object({
            headerBackground: z.boolean().optional(),
            headerUnderline: z.boolean().optional(),
            firmName: z.boolean().optional(),
            sectionTabHighlights: z.boolean().optional(),
            sectionHeadings: z.boolean().optional(),
            buttons: z.boolean().optional(),
          })
          .optional(),
        headerTextColor: z.string().optional(),
        backgroundColor: z.string().optional(),
        headerDisplay: z
          .object({
            firmLogo: z.boolean().optional(),
            firmName: z.boolean().optional(),
          })
          .optional(),
        buttonRadius: z.string().optional(),
        inputRadius: z.string().optional(),
        logo: z.string().optional(),
      })
      .optional(),
    voiceGuide: z
      .object({
        enabled: z.boolean().optional(),
      })
      .optional(),
    hasPendingDigitalSignature: z.boolean().optional(),
    signingUrl: z.string().optional(),
  }),
};

export const submitLambdaSchema = {
  input: z.object({
    submissionUri: z.string(),
    formUri: z.string(),
    teamUri: z.string(),
    lastUpdatedFieldUri: z.string().optional(),
    lastUpdatedSectionUri: z.string().optional(),
    status: submissionStatusSchema.optional(),
    responses: z.record(z.any()),
    aiFillFiles: z.array(z.string()).optional(),
    autofillFeedback: z.string().optional(),
    captchaToken: z.string().optional(),
    hasDynamicOptions: z.boolean().optional(),
  }),
  output: z.object({
    operationIds: z.array(z.string()),
    submissionStatus: z.object({
      status: submissionStatusSchema.optional(),
      deleted: z.boolean().optional(),
    }),
    fieldsWithDynamicOptions: loadedFormSchema.shape.fields.optional(),
    error: z.string().optional(),
    digitalSignature: z
      .object({
        signingUrl: z.string().optional(),
      })
      .optional(),
  }),
};

export const searchAddressLambdaSchema = {
  input: z.object({
    search: z.string(),
    sessionKey: z.string().optional(),
  }),
  output: z.object({
    addresses: z.array(addressPredictionSchema),
  }),
};

export const getAddressLambdaSchema = {
  input: z.object({
    placeId: z.string(),
    sessionKey: z.string().optional(),
  }),
  output: addressSchema,
};

export const uploadFileLambdaSchema = {
  input: z.object({
    fileNames: z.array(z.string()),
    submissionUri: z.string(),
    formUri: z.string(),
    fieldUri: z.string(),
    teamUri: z.string(),
  }),
  output: z.array(
    z.object({
      key: z.string(),
      downloadUrl: z.string(),
      uploadUrl: z.string(),
    }),
  ),
};

export const captchaLambdaSchema = {
  input: z.object({
    token: z.string(),
  }),
  output: z.object({
    success: z.boolean(),
  }),
};

export const signNotifyLambdaSchema = {
  input: z.object({
    ConsumerKey: z.string(),
  }),
  output: z.object({}),
};

export const getStaffLambdaSchema = {
  input: z.object({
    accountId: z.string(),
    id: z.string(),
  }),
  output: z.object({
    id: z.string().nullable(),
    userId: z.string().nullable(),
    firstName: z.string().nullable(),
    middleName: z.string().nullable(),
    lastName: z.string().nullable(),
    enabled: z.boolean(),
  }),
};

export const analyticsEventLambdaSchema = {
  input: z.object({
    id: z.string(),
    platform: z.enum(["Web"]).optional(),
    operatingSystem: z.string().optional(),
    productVersion: z.string().optional(),
    browserType: z.string().optional(),
    eventDateTime: z.number(),
    sessionId: z.string().optional(),
    event: z.object({
      category: z.enum(["identify", "track"]),
      name: z.string().optional().nullable(),
      screen: z.string().optional().nullable(),
      entryPoint: z.string().optional().nullable(),
      component: z.string().optional().nullable(),
      jiraKey: z.string().optional().nullable(),
      interactionType: z.enum(["Active", "Passive"]).optional(),
      properties: z.record(z.any()).optional(),
    }),
  }),
  output: z.object({
    operationId: z.string(),
  }),
};

export const apiSchemas = buildApiGatewaySchemas(apiGateway, {
  createSubmission: createSubmissionLambdaSchema,
  load: loadLambdaSchema,
  submit: submitLambdaSchema,
  searchAddress: searchAddressLambdaSchema,
  getAddress: getAddressLambdaSchema,
  uploadFile: uploadFileLambdaSchema,
  captcha: captchaLambdaSchema,
  analyticsEvent: analyticsEventLambdaSchema,
  getStaff: getStaffLambdaSchema,
  signNotify: signNotifyLambdaSchema,
});
